import React, { useEffect, useMemo, useState } from 'react';
import './EncuestasComponent.css';
import { useNavigate } from 'react-router-dom';
import {
  API_BASE_URL,
  CreateRedsysPayment,
  DeleteVote,
  GetAccountByTokenRaw,
  GetAllPolls,
  GetPollRegistrationSummary,
  GetRedsysStatus,
  SavePollRegistrationCart,
  getToken,
} from '../../Backend/ApiCalls';
import { Notification } from '../NotificationLoginComponent';
import { BottomNav } from '../BottomNav';
import { savePendingRedsysRedirect } from './PagoEncuestaRedirectComponent';
import { formatCents } from '../../utils/formatMoney';
import { FormattedText } from '../Common/FormattedText';
import { PaymentStatusBadge } from '../Common/PaymentStatusBadge';
import { hasPaymentConfiguration, parsePaymentConfiguration } from '../../utils/paymentConfiguration';
import { getPrimaryProfileToken, getSecondaryProfileTokens } from '../../utils/profileStorage';

// Clave de localStorage para la operación de pago pendiente (ver Parte 6/7):
// sobrevive a que el usuario cierre el navegador externo, deje la app en
// segundo plano o la cierre del todo. Solo guardamos un identificador de
// operación, nunca datos de Redsys ni del banco.
const PENDING_PAYMENT_KEY = 'pending_redsys_payment';

// No basta con saber que estamos dentro de Capacitor (isNativePlatform):
// Capacitor.isPluginAvailable() comprueba que el plugin nativo esté
// REALMENTE registrado en el binario instalado, no solo que exista el
// wrapper de JS. Función pura y exportada para poder testear los distintos
// casos (web, Capacitor sin el plugin, Capacitor con el plugin) sin montar
// el componente completo.
export const canUseSystemBrowserPlugin = (capacitorGlobal) =>
  Boolean(capacitorGlobal?.isPluginAvailable && capacitorGlobal.isPluginAvailable('Browser'));

// Desplegable accesible y compacto, cerrado por defecto. Se usa dos veces en
// el modal de pago (info del pago, diferencia entre "casal"/"pagar después")
// para no tener textos largos siempre visibles ocupando la pantalla en móvil.
const PayDisclosure = ({ id, icon, summary, children, compact = false }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={`pay-disclosure${compact ? ' pay-disclosure-compact' : ''}`}>
      <button
        type="button"
        className="pay-disclosure-trigger"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="pay-disclosure-icon" aria-hidden="true">{icon}</span>
        <span className="pay-disclosure-summary">{summary}</span>
        <span className={`pay-disclosure-chevron${open ? ' is-open' : ''}`} aria-hidden="true">⌄</span>
      </button>
      <div
        id={id}
        role="region"
        className={`pay-disclosure-panel${open ? ' is-open' : ''}`}
      >
        <div className="pay-disclosure-panel-inner">{children}</div>
      </div>
    </div>
  );
};

export const EncuestaComponent = (props) => {
  const { user } = props;
  const [encuestas, setEncuestas] = useState([]);
  const [activePollCount, setActivePollCount] = useState(0);
  const [familyAccounts, setFamilyAccounts] = useState([]);
  const [activeDetalles, setActiveDetalles] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [isErrorNoti, setIsErrorNoti] = useState(false);
  const navigate = useNavigate();

  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [isRegistrationModalClosing, setIsRegistrationModalClosing] = useState(false);
  const [registrationPoll, setRegistrationPoll] = useState(null);
  const [registrationCandidates, setRegistrationCandidates] = useState([]);
  const [isRegistrationLoading, setIsRegistrationLoading] = useState(false);
  // 'idle' | 'preparing' | 'opening' — para que el botón de pago muestre
  // "Preparando pago…" / "Abriendo navegador…" en vez de quedarse mudo
  // mientras se crea la operación y se abre el navegador externo.
  const [paymentStage, setPaymentStage] = useState('idle');
  const [registrationSummaryByPoll, setRegistrationSummaryByPoll] = useState({});
  const [loadingRegistrationSummaryByPoll, setLoadingRegistrationSummaryByPoll] = useState({});

  const safeJson = (val, fallback) => {
    try {
      if (typeof val === 'string') return JSON.parse(val);
      return val ?? fallback;
    } catch {
      return fallback;
    }
  };

  const parsePayCfg = parsePaymentConfiguration;

  const ageLabel = (t) =>
    t === 0 ? 'Exento' :
    t === 1 ? 'Infantil' :
    t === 2 ? 'Juvenil' :
    t === 3 ? 'Adulto' : 'Desconocido';

  const paymentStatusLabel = (row) => {
    if (!row) return 'No inscrito';
    if (row.attendanceStatus !== 'registered') return 'No inscrito';
    if (row.paymentStatus === 'paid') return row.paymentMethod === 'bizum' ? 'Pagado con Bizum' : 'Pagado';
    if (row.paymentStatus === 'pending_manual') return 'Paga en casal';
    if (row.paymentStatus === 'pending_online') return 'Pendiente online';
    if (row.paymentStatus === 'free') return 'Gratis';
    if (row.paymentStatus === 'unpaid') return 'Sin pagar';
    return 'Pendiente de pago';
  };

  const paymentStatusClass = (row) => {
    if (!row || row.attendanceStatus !== 'registered') return 'status-idle';
    if (row.paymentStatus === 'paid' || row.paymentStatus === 'free') return 'status-paid';
    if (row.paymentStatus === 'pending_manual') return 'status-manual';
    if (row.paymentStatus === 'pending_online') return 'status-online';
    return 'status-pending';
  };

  // amountCents SIEMPRE en céntimos (así los guarda y devuelve el backend).
  const formatMoney = (amountCents) => formatCents(amountCents);

  // Para encuestas tipo "signup": el precio se respeta tal cual lo configura
  // el admin (incluido 0 = exento). NO hay fallback al máximo: si quieres que
  // todas las edades paguen lo mismo, usa el input "Precio común" del form.
  const signupAmountForAge = (payCfg, ageType) => {
    const prices = payCfg?.pricesByAgeType || {};
    return Number(prices[String(ageType)] || 0);
  };

  // Devuelve el precio (en céntimos) de una opción para una edad dada.
  // Estricto: se usa `pricesByAgeType[ageType]` tal cual. Si vale 0, es 0
  // (exención explícita). Para compatibilidad con datos legacy sin
  // pricesByAgeType cae a `amountCents`.
  const optionAmountCents = (option, ageType = null) => {
    const pricesByAgeType = option?.pricesByAgeType || option?.text?.pricesByAgeType || null;
    if (pricesByAgeType && ageType != null) {
      const agePrice = Number(pricesByAgeType[String(ageType)] || 0);
      return Number.isFinite(agePrice) && agePrice >= 0 ? agePrice : 0;
    }
    const legacy = Number(option?.amountCents ?? option?.text?.amountCents ?? 0);
    return Number.isFinite(legacy) && legacy > 0 ? legacy : 0;
  };

  const optionText = (option) =>
    typeof option?.text === 'string'
      ? option.text
      : (option?.text?.text ?? String(option?.id ?? ''));

  const getPaidItemsForVote = (encuesta, vote, ageType = null) => {
    const responses = Array.isArray(vote?.responses) ? vote.responses : [];
    return responses.flatMap((responseItem) => {
      const question = (encuesta.questions || []).find(
        (q) => String(q.id) === String(responseItem.questionId)
      );
      if (!question || question.type === 'text') return [];

      const selectedIds = new Set((Array.isArray(responseItem.response) ? responseItem.response : [responseItem.response])
        .filter((value) => value != null)
        .map((value) => String(value)));

      return (question.options || [])
        .filter((option) => selectedIds.has(String(option.id)) && optionAmountCents(option, ageType) > 0)
        .map((option) => ({
          label: optionText(option),
          amountCents: optionAmountCents(option, ageType),
        }));
    });
  };

  const getVotePaymentAmount = (encuesta, vote, ageType = null) =>
    getPaidItemsForVote(encuesta, vote, ageType).reduce((sum, item) => sum + Number(item.amountCents || 0), 0);

  const paymentConceptLabel = (row, fallback = '-') => {
    const items = Array.isArray(row?.paidItems) ? row.paidItems : [];
    if (items.length) {
      return items.map((item) => `${item.label} ${formatMoney(item.amountCents)}`).join(', ');
    }
    return row?.conceptLabel || fallback;
  };

  const paymentMethodLabel = (paymentMethod) =>
    paymentMethod === 'bizum' ? 'Bizum'
      : paymentMethod === 'manual' ? 'Casal'
        : paymentMethod === 'online' ? 'Tarjeta'
          : paymentMethod === 'free' ? 'Gratis'
            : '-';

  const getManualPaymentWindows = (encuesta) => {
    const windows = parsePayCfg(encuesta?.paymentConfig)?.manualPaymentWindows;
    return Array.isArray(windows) ? windows
      .filter((item) => item?.date && item?.startTime && item?.endTime && item.endTime > item.startTime)
      .sort((a, b) => `${a.date} ${a.startTime}`.localeCompare(`${b.date} ${b.startTime}`)) : [];
  };

  const buildDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const d = String(dateStr).includes('T') ? String(dateStr).split('T')[0] : String(dateStr);
    const dt = new Date(`${d}T00:00:00`);
    const [hh, mm, ss] = String(timeStr).split(':');
    dt.setHours(Number(hh || 0), Number(mm || 0), Number(ss || 0));
    return dt;
  };

  const isNativeApp = () =>
    Boolean(window?.Capacitor?.isNativePlatform && window.Capacitor.isNativePlatform());

  // ===== Recuperación de pago pendiente (Parte 6/7) =====
  // Solo guardamos lo mínimo: identificador de operación, pedido, encuesta y
  // cuándo se inició. Nada de datos de Redsys ni del banco.
  const savePendingPaymentLocal = (info) => {
    try {
      localStorage.setItem(PENDING_PAYMENT_KEY, JSON.stringify(info));
    } catch (error) {
      console.warn('No se pudo guardar la operación de pago pendiente:', error);
    }
  };

  const loadPendingPaymentLocal = () => {
    try {
      const raw = localStorage.getItem(PENDING_PAYMENT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const clearPendingPaymentLocal = () => {
    try {
      localStorage.removeItem(PENDING_PAYMENT_KEY);
    } catch (error) {
      console.warn('No se pudo limpiar la operación de pago pendiente:', error);
    }
  };

  const [verifyingPendingPayment, setVerifyingPendingPayment] = useState(false);

  // Consulta el estado REAL en el backend de cualquier operación pendiente
  // guardada localmente. Nunca crea una operación nueva ni da nada por
  // pagado por sí misma: solo lee lo que el backend confirme.
  const checkPendingPayment = async () => {
    const pending = loadPendingPaymentLocal();
    if (!pending?.order) return;

    setVerifyingPendingPayment(true);
    try {
      const { response, data } = await GetRedsysStatus(pending.order);
      if (!response.ok) {
        // Fallo de red/servidor puntual: no borramos el pendiente, se
        // reintentará en el próximo focus/visibilitychange.
        return;
      }

      const status = data?.intent?.status;
      // Además del aviso, si tenemos receiptToken navegamos al recibo
      // dentro de la propia app (Parte 15): mismo componente/pantalla que
      // se ve al volver de Redsys, pero sin salir de la sesión activa. Sin
      // receiptToken (pendientes guardados antes de este cambio) se queda
      // solo con el aviso, como antes.
      const goToReceipt = (mode) => {
        if (!pending.receiptToken) return;
        navigate(
          `/${mode === 'ok' ? 'pago-ok' : 'pago-ko'}?order=${encodeURIComponent(pending.order)}&receiptToken=${encodeURIComponent(pending.receiptToken)}`
        );
      };

      if (status === 'paid') {
        clearPendingPaymentLocal();
        showNoti('Pago confirmado. Gracias por tu inscripción.', false, 6000);
        if (pending.pollId) {
          loadRegistrationSummary(pending.pollId, familyDnis, true).catch(() => {});
        }
        goToReceipt('ok');
      } else if (status === 'failed') {
        clearPendingPaymentLocal();
        showNoti(
          'No se ha podido completar el pago. Puedes intentarlo de nuevo o pagar presencialmente en el casal.',
          true,
          6500
        );
        goToReceipt('ko');
      } else if (status === 'cancelled') {
        clearPendingPaymentLocal();
        showNoti('Se canceló el pago. Puedes intentarlo de nuevo cuando quieras.', true, 5500);
      }
      // Si sigue 'pending' lo dejamos tal cual: no creamos otra operación,
      // simplemente se volverá a comprobar en el próximo intento.
    } catch (error) {
      console.error('checkPendingPayment error:', error);
    } finally {
      setVerifyingPendingPayment(false);
    }
  };

  // Vuelve a comprobar el pago pendiente cada vez que la app recupera el
  // foco (vuelve del navegador externo, se reabre tras cerrarse, o el
  // usuario cambia de pestaña y vuelve). No usa ningún plugin nuevo: solo
  // visibilitychange/focus, ya disponibles en cualquier WebView o navegador.
  useEffect(() => {
    checkPendingPayment();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkPendingPayment();
    };
    const handleFocus = () => checkPendingPayment();

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchEncuestas = async () => {
      try {
        const { data } = await GetAllPolls();
        const now = new Date();

        const activeEncuestas = (data || []).filter((encuesta) => {
          const closeDT = buildDateTime(encuesta.closeDate, encuesta.closeTime);
          const eventDT = buildDateTime(encuesta.date, encuesta.time);
          const deadline = closeDT || eventDT;
          if (!deadline) return false;
          return Number(encuesta.isPublished) === 1 && deadline >= now;
        });

        // La hora del evento es su inicio, no el instante en que debe ocultarse
        // el resumen: se conserva hasta el final de ese día local.
        const answeredEncuestas = (data || []).filter((encuesta) => {
          const closeDT = buildDateTime(encuesta.closeDate, encuesta.closeTime)
            || buildDateTime(encuesta.date, encuesta.time);
          if (Number(encuesta.isPublished) !== 1 || !closeDT || closeDT >= now) return false;
          const voted = safeJson(encuesta.voted, []);
          const answeredByActiveProfile = Array.isArray(voted)
            && voted.some((vote) => String(vote?.dni) === String(user?.dni));
          if (!answeredByActiveProfile || !encuesta.date) return false;
          const eventDay = String(encuesta.date).includes('T') ? String(encuesta.date).split('T')[0] : encuesta.date;
          const eventEnd = new Date(`${eventDay}T00:00:00`);
          eventEnd.setHours(23, 59, 59, 999);
          return !Number.isNaN(eventEnd.getTime()) && eventEnd >= now;
        });

        const normalizePoll = (e, historical = false) => {
          const voted = safeJson(e.voted, []);
          const questions = safeJson(e.questions, []);
          return {
            ...e,
            id: e.id ?? e.Id,
            voted: Array.isArray(voted) ? voted : [],
            questions: Array.isArray(questions) ? questions : [],
            expanded: false,
            historical,
          };
        };

        setActivePollCount(activeEncuestas.length);
        setEncuestas([
          ...activeEncuestas.map((poll) => normalizePoll(poll)),
          ...answeredEncuestas.map((poll) => normalizePoll(poll, true)),
        ]);
      } catch (error) {
        console.error('Error fetching surveys', error);
      }
    };

    fetchEncuestas();
  }, [user?.dni]);

  // Precarga ligera del resumen de pago del propio usuario (Parte 2/3): antes
  // solo se pedía al backend al abrir el acordeón de una encuesta concreta,
  // así que la tarjeta cerrada no podía mostrar "pagado/pendiente/casal" de
  // un vistazo. Reutiliza tal cual loadRegistrationSummary/GetPollRegistrationSummary
  // (mismo endpoint, mismo cálculo del backend); lo único que cambia es que se
  // pide un poco antes y solo con el DNI propio (no el de toda la familia,
  // que ya se carga bajo demanda al abrir la tarjeta o el modal de pago).
  useEffect(() => {
    if (!user?.dni || !encuestas.length) return;

    encuestas.forEach((encuesta) => {
      const votedByUser = (encuesta.voted || []).some((vote) => String(vote?.dni) === String(user.dni));
      const paymentEnabled = hasPaymentConfiguration(encuesta);
      const alreadyLoaded = registrationSummaryByPoll[encuesta.id] || loadingRegistrationSummaryByPoll[encuesta.id];

      if (votedByUser && paymentEnabled && !alreadyLoaded) {
        loadRegistrationSummary(encuesta.id, [String(user.dni)]).catch(() => {});
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encuestas, user?.dni]);

  useEffect(() => {
    const preloadFamilyAccounts = async () => {
      try {
        const accounts = await loadAccountsFromTokens();
        setFamilyAccounts(accounts);
      } catch (error) {
        console.error('Error loading linked accounts', error);
      }
    };

    preloadFamilyAccounts();
  }, []);

  const resizeOpenPanel = () => {
    if (!activeDetalles) return;
    const el = document.getElementById(activeDetalles);
    if (!el) return;

    if (el.style.height === 'auto') {
      const computed = getComputedStyle(el).height;
      el.style.height = computed;
    }

    requestAnimationFrame(() => {
      el.style.height = `${el.scrollHeight}px`;
      const onEnd = (ev) => {
        if (ev.propertyName === 'height') {
          el.style.height = 'auto';
          el.removeEventListener('transitionend', onEnd);
        }
      };
      el.addEventListener('transitionend', onEnd);
    });
  };

  useEffect(() => {
    resizeOpenPanel();
  }, [encuestas, activeDetalles]);

  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = (timeString || '').split(':');
    return `${hours ?? '00'}:${minutes ?? '00'}`;
  };

  // Fecha+hora compacta es-ES sin segundos: "28/07/2026, 22:11" en vez del
  // "28/7/2026, 22:11:12" que da toLocaleString('es-ES') sin opciones.
  const formatDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const showNoti = (msg, isErr = false, ms = 5000) => {
    setNotificationMessage(msg);
    setIsErrorNoti(isErr);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), ms);
  };

  const updateEncuestaVoted = (pollId, voted) => {
    setEncuestas((prev) =>
      prev.map((e) => (e.id === pollId ? { ...e, voted: Array.isArray(voted) ? voted : e.voted } : e))
    );
  };

  const borrarDeEncuesta = async (encuestaId) => {
    try {
      const { response, data } = await DeleteVote(encuestaId, user.dni);

      if (!response.ok) {
        console.error('Error al eliminar el voto:', data);
        showNoti(props.I18n.ErrorMessageDeleteVote, true);
      } else {
        showNoti(props.I18n.SuccessMessageDeleteVote, false);
        setEncuestas((prev) =>
          prev.map((e) =>
            e.id === encuestaId
              ? { ...e, voted: (e.voted || []).filter((v) => v.dni !== user.dni) }
              : e
          )
        );
        if (activeDetalles) setTimeout(() => resizeOpenPanel(), 50);
      }
    } catch (error) {
      console.error('Error en borrarDeEncuesta:', error);
      showNoti(props.I18n.ErrorMessageDeleteVote, true);
    }
  };

  const onClick = (encuesta) => {
    const encuestaDetalleId = `encuesta-detalle-${encuesta.id}`;
    const el = document.getElementById(encuestaDetalleId);
    const current = document.getElementById(activeDetalles);

    if (activeDetalles && activeDetalles !== encuestaDetalleId && current) {
      current.style.height = '0px';
    }
    if (!el) return;

    const isClosed = !el.clientHeight;

    if (isClosed) {
      el.style.height = '0px';

      el.style.height = `${el.scrollHeight}px`;
      setActiveDetalles(encuestaDetalleId);
      if (hasPayment(encuesta)) {
        const accountLoader = familyAccounts.length
          ? Promise.resolve(familyAccounts)
          : loadAccountsFromTokens();

        accountLoader
          .then((accounts) => {
            if (!familyAccounts.length && accounts.length) {
              setFamilyAccounts(accounts);
            }

            return loadRegistrationSummary(
              encuesta.id,
              accounts.map((account) => String(account.dni)).filter(Boolean)
            );
          })
          .catch((error) => {
            console.error('loadRegistrationSummary error:', error);
          });
      }

      const onEnd = (ev) => {
        if (ev.propertyName === 'height') {
          el.style.height = 'auto';
          el.removeEventListener('transitionend', onEnd);
        }
      };
      el.addEventListener('transitionend', onEnd);
    } else {
      const currentPx = getComputedStyle(el).height;
      el.style.height = currentPx;

      el.style.height = '0px';
      setActiveDetalles(null);
    }
  };

  const isUserVoted = (encuesta) =>
    (encuesta.voted || []).some((vote) => String(vote?.dni) === String(user?.dni));
  const handleAnswerSurvey = (pollId) => navigate(`/contestar-encuesta/${pollId}`);

  const getUserAnswers = (encuesta) => {
    const userVote = (encuesta.voted || []).find((vote) => vote.dni === user.dni);
    if (!userVote || !userVote.responses) return [];

    return userVote.responses
      .map((res) => {
        const question = (encuesta.questions || []).find((q) => q.id === res.questionId);
        if (!question || !res.response || res.response.length === 0) return null;

        if (typeof res.response[0] === 'string') {
          return { questionText: question.text, response: res.response[0] };
        }

        if (typeof res.response[0] === 'number') {
          const selectedOptions = res.response
            .map((optionId) => (question.options || []).find((opt) => opt.id === optionId)?.text)
            .filter(Boolean);

          return { questionText: question.text, response: selectedOptions.join(', ') };
        }

        return null;
      })
      .filter(Boolean);
  };

  const getVoteByDni = (encuesta, dni) =>
    (encuesta.voted || []).find((vote) => String(vote?.dni) === String(dni));

  const familyDnis = useMemo(
    () => [...new Set([String(user?.dni || ''), ...familyAccounts.map((account) => String(account?.dni || ''))].filter(Boolean))],
    [familyAccounts, user?.dni]
  );

  const familyDniSet = useMemo(() => new Set(familyDnis), [familyDnis]);

  const loadAccountsFromTokens = async () => {
    const principalToken = getPrimaryProfileToken();
    const secondary = getSecondaryProfileTokens();
    const tokens = [...new Set([principalToken, ...secondary].filter(Boolean))];
    const accounts = [];

    for (const token of tokens) {
      const { response, data } = await GetAccountByTokenRaw(token);
      if (response.ok && data?.dni) {
        accounts.push(data);
      }
    }

    return accounts;
  };

  const loadRegistrationSummary = async (pollId, accountDnis = [], force = false) => {
    if (!force && registrationSummaryByPoll[pollId]) {
      return registrationSummaryByPoll[pollId];
    }

    setLoadingRegistrationSummaryByPoll((prev) => ({ ...prev, [pollId]: true }));
    try {
      // El backend resuelve los DNIs a partir de tokens válidos; los DNIs
      // locales solo siguen siendo útiles para pintar la familia en el modal.
      const profileTokens = [...new Set([
        getPrimaryProfileToken(),
        ...getSecondaryProfileTokens(),
      ].filter(Boolean))];
      const { response, data } = await GetPollRegistrationSummary(pollId, profileTokens);
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Tu sesión ha caducado o no hemos podido identificar tu perfil. Vuelve a iniciar sesión.');
        }
        if (response.status >= 500) {
          throw new Error('No se ha podido cargar el resumen de pago. Inténtalo de nuevo más tarde.');
        }
        throw new Error(data?.Error || 'No se ha podido cargar el resumen de pago.');
      }

      setRegistrationSummaryByPoll((prev) => ({ ...prev, [pollId]: data }));
      return data;
    } finally {
      setLoadingRegistrationSummaryByPoll((prev) => ({ ...prev, [pollId]: false }));
    }
  };

  const openRegistrationModal = async (encuesta) => {
    try {
      setIsRegistrationLoading(true);
      setIsRegistrationModalClosing(false);
      const payCfg = parsePayCfg(encuesta.paymentConfig);
      const isFreeSignupFlow = isSignupPoll(encuesta) && !hasPayment(encuesta);
      const accounts = familyAccounts.length ? familyAccounts : await loadAccountsFromTokens();
      if (!familyAccounts.length && accounts.length) {
        setFamilyAccounts(accounts);
      }
      const accountDnis = accounts.map((account) => String(account.dni)).filter(Boolean);
      const data = await loadRegistrationSummary(encuesta.id, accountDnis, true);
      const rows = data?.registrations || [];
      const rowByDni = new Map(rows.map((row) => [String(row.beneficiaryDni), row]));
      const votedByDni = new Set((encuesta.voted || []).map((vote) => String(vote?.dni)));
      const manageOnlyPending = hasPayment(encuesta) && (isUserVoted(encuesta) || !isSignupPoll(encuesta));

      const candidates = accounts.map((account) => {
        const vote = getVoteByDni(encuesta, account.dni);
        const persistedRow = rowByDni.get(String(account.dni));
        const amountCents = payCfg?.enabled
          ? (isSignupPoll(encuesta)
            ? signupAmountForAge(payCfg, account.ageType)
            : getVotePaymentAmount(encuesta, vote, Number(account.ageType ?? vote?.ageType ?? 3)))
          : 0;
        const paidItemsForAccount = isSignupPoll(encuesta)
          ? []
          : getPaidItemsForVote(encuesta, vote, Number(account.ageType ?? vote?.ageType ?? 3));
        const row = persistedRow || (
          vote ? {
            beneficiaryDni: String(account.dni),
            beneficiaryName: account.name || vote?.name || '',
            beneficiarySurname: account.surname || vote?.surname || '',
            payerDni: String(account.dni),
            ageType: Number(account.ageType ?? vote?.ageType ?? 3),
            amountCents,
            paidItems: paidItemsForAccount,
            conceptLabel: isSignupPoll(encuesta)
              ? encuesta.title
              : paidItemsForAccount.map((item) => item.label).join(', ') || 'Sin opciones de pago',
            attendanceStatus: 'registered',
            paymentStatus: amountCents > 0 ? 'unpaid' : 'free',
            paymentMethod: amountCents > 0 ? null : 'free',
            paymentIntentId: null,
            reservedAt: null,
            paidAt: null,
            virtualFromVote: true,
          }
            : null
        );
        const isRegistered = row?.attendanceStatus === 'registered' || Boolean(vote);
        const hasPendingPayment = isRegistered
          && !['paid', 'free'].includes(row?.paymentStatus)
          && Number(row?.amountCents ?? amountCents) > 0;
        const isRelevantForSurvey = isSignupPoll(encuesta)
          ? (!manageOnlyPending || isRegistered)
          : votedByDni.has(String(account.dni));

        if (manageOnlyPending && (!isRelevantForSurvey || !hasPendingPayment)) {
          return null;
        }

        return {
          dni: String(account.dni),
          name: account.name,
          surname: account.surname,
          ageType: Number(account.ageType ?? 3),
          amountCents: Number.isFinite(Number(row?.amountCents ?? amountCents))
            ? Number(row?.amountCents ?? amountCents)
            : 0,
          selected: manageOnlyPending
            ? hasPendingPayment
            : (row ? row.attendanceStatus === 'registered' : (isFreeSignupFlow && String(account.dni) === String(user.dni))),
          status: row || null,
        };
      }).filter(Boolean);

      if (!candidates.length) {
        showNoti(
          manageOnlyPending
            ? 'No hay perfiles pendientes de pago para esta encuesta.'
            : 'No hay perfiles disponibles para gestionar en esta encuesta.',
          true,
          4500
        );
        return;
      }

      setRegistrationPoll(encuesta);
      setRegistrationCandidates(candidates);
      setShowRegistrationModal(true);
    } catch (error) {
      console.error('openRegistrationModal error:', error);
      // Si loadRegistrationSummary ya trae un motivo concreto (sesión no
      // identificada, encuesta no encontrada, etc.) lo mostramos tal cual en
      // vez de taparlo siempre con un mensaje genérico.
      showNoti(error?.message || 'No se ha podido preparar la inscripción.', true);
    } finally {
      setIsRegistrationLoading(false);
    }
  };

  const toggleCandidate = (dni) => {
    setRegistrationCandidates((prev) =>
      prev.map((candidate) =>
        candidate.dni === dni ? { ...candidate, selected: !candidate.selected } : candidate
      )
    );
  };

  const selectedCandidates = useMemo(
    () => registrationCandidates.filter((candidate) => candidate.selected),
    [registrationCandidates]
  );

  const payTotalCents = useMemo(
    () => selectedCandidates.reduce((sum, candidate) => sum + Number(candidate.amountCents || 0), 0),
    [selectedCandidates]
  );

  const payableSelectedCandidates = useMemo(
    () =>
      selectedCandidates.filter((candidate) => {
        const status = candidate.status;
        if (!status || status.attendanceStatus !== 'registered') {
          return Number(candidate.amountCents || 0) > 0;
        }
        return !['paid', 'free'].includes(status.paymentStatus) && Number(candidate.amountCents || 0) > 0;
      }),
    [selectedCandidates]
  );

  // Envía el formulario de Redsys directamente en esta pestaña/WebView. Es
  // el único método que sabemos con certeza que funciona en la app YA
  // instalada (es el que había antes de intentar el navegador externo).
  const submitRedsysInline = (fields, url) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;

    Object.entries(fields).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  const submitRedsys = async ({ redsysPayload, intentId, pollId, order, bridgeToken, receiptToken, gatewayMethod = 'card' }) => {
    const url = redsysPayload?.url;
    const fields = redsysPayload?.fields;
    if (!url || !fields?.Ds_MerchantParameters || !fields?.Ds_Signature) {
      showNoti('La respuesta de Redsys viene incompleta. Revisa la configuración del backend.', true, 6000);
      return;
    }

    console.log('[FRONT][REDSYS][submitRedsys] url:', url);
    console.log('[FRONT][REDSYS][submitRedsys] fieldNames:', Object.keys(fields || {}));

    // Guardamos el identificador mínimo de la operación ANTES de abrir el
    // navegador/enviar el formulario: si el usuario cierra la app o el
    // navegador a medias, al volver podremos recuperar el estado real sin
    // crear una operación nueva (Parte 6/7). receiptToken se guarda también
    // para poder abrir la pantalla de recibo (/pago-ok, /pago-ko) dentro de
    // la app en cuanto se confirme el pago (Parte 15).
    savePendingPaymentLocal({ order, intentId, pollId, receiptToken, startedAt: Date.now() });

    if (isNativeApp()) {
      // IMPORTANTE: no basta con saber que estamos dentro de Capacitor.
      // Capacitor.isPluginAvailable() comprueba que el plugin nativo esté
      // REALMENTE registrado en el binario instalado, no solo que exista el
      // wrapper de JS. Una app ya instalada puede llevar un bundle web más
      // nuevo que el binario nativo (o simplemente no llevar este plugin
      // compilado) — intentar llamarlo igualmente producía el error real
      // "$BROWSER_PLUGIN does not implement...", capturado más abajo.
      const browserPluginAvailable = canUseSystemBrowserPlugin(window?.Capacitor);

      if (browserPluginAvailable && bridgeToken) {
        try {
          const { Browser } = await import('@capacitor/browser');
          const bridgeUrl =
            `${API_BASE_URL}/Payments/Redsys/Bridge/${encodeURIComponent(intentId)}` +
            `?token=${encodeURIComponent(bridgeToken)}&gatewayMethod=${encodeURIComponent(gatewayMethod)}&returnMode=app`;
          console.log('[FRONT][REDSYS][submitRedsys] abriendo con @capacitor/browser:', bridgeUrl);
          await Browser.open({ url: bridgeUrl });
          return;
        } catch (error) {
          // No es un error fatal: significa que este plugin tampoco está
          // disponible de verdad en este binario. Seguimos con el flujo que
          // sabemos que funciona, sin asustar al usuario con un mensaje de
          // error para algo que sí vamos a completar igualmente.
          console.error('[FRONT][REDSYS] Browser.open no disponible, uso el flujo dentro de la app:', error);
        }
      } else {
        console.warn('[FRONT][REDSYS] Plugin "Browser" no registrado en esta instalación; uso el flujo dentro de la app.');
      }

      // Fallback: mismo flujo que existía antes de intentar el navegador
      // externo (form auto-enviado dentro de la propia WebView). El 3D
      // Secure se completa dentro de la app; si el banco lo rechaza (caso
      // detectado con Revolut), el usuario siempre puede pagar en el casal.
      try {
        savePendingRedsysRedirect({ ...redsysPayload, gatewayMethod });
        navigate('/pago-redsys');
      } catch (fallbackError) {
        console.error('[FRONT][REDSYS] Fallo también el flujo interno:', fallbackError);
        clearPendingPaymentLocal();
        showNoti(
          'No se ha podido abrir el pago. Puedes intentarlo de nuevo o realizarlo desde la web.',
          true,
          7000
        );
      }
      return;
    }

    // Web normal (no app): submit POST directo a Redsys, en la misma pestaña.
    submitRedsysInline(fields, url);
  };

  const saveRegistrationCart = async (paymentChoice = 'none') => {
    if (!registrationPoll) return;

    try {
      setIsRegistrationLoading(true);
      const registrants = selectedCandidates.map((candidate) => ({
        dni: candidate.dni,
        name: candidate.name,
        surname: candidate.surname,
        ageType: candidate.ageType,
      }));

      const { response, data } = await SavePollRegistrationCart(registrationPoll.id, registrants, paymentChoice);
      if (!response.ok) {
        showNoti(data?.Error || 'No se ha podido guardar la inscripción.', true);
        return;
      }

      setRegistrationSummaryByPoll((prev) => ({
        ...prev,
        [registrationPoll.id]: {
          ...(prev[registrationPoll.id] || {}),
          pollId: registrationPoll.id,
          conceptLabel: data?.conceptLabel || prev[registrationPoll.id]?.conceptLabel || registrationPoll.title,
          registrations: data.registrations || [],
        },
      }));
      updateEncuestaVoted(registrationPoll.id, data.voted);
      setRegistrationCandidates((prev) =>
        prev.map((candidate) => {
          const row = (data.registrations || []).find(
            (registration) => String(registration.beneficiaryDni) === String(candidate.dni)
          );
          return {
            ...candidate,
            status: row || null,
            selected: row ? row.attendanceStatus === 'registered' : false,
          };
        })
      );
      setShowRegistrationModal(false);
      setIsRegistrationModalClosing(false);
      showNoti('Inscripción guardada correctamente.', false);
    } catch (error) {
      console.error('saveRegistrationCart error:', error);
      showNoti('No se ha podido guardar la inscripción.', true);
    } finally {
      setIsRegistrationLoading(false);
    }
  };

  const doPay = async (gatewayMethod = 'card') => {
    if (!registrationPoll) return;
    if (!selectedCandidates.length) {
      showNoti('Selecciona al menos una persona.', true, 3500);
      return;
    }
    if (!payableSelectedCandidates.length) {
      showNoti('No hay importes pendientes para las personas seleccionadas.', true, 3500);
      return;
    }

    // El backend identifica quién paga a través de este token (perfil activo).
    // Si no hay token no tiene sentido ni intentar la petición: mostramos un
    // aviso claro en vez de dejar que el servidor la rechace con un 403 que
    // el usuario no sabría interpretar.
    if (!getToken()) {
      showNoti(
        'No se ha podido identificar el perfil seleccionado. Vuelve a iniciar sesión o selecciona de nuevo el perfil e inténtalo otra vez.',
        true,
        6000
      );
      return;
    }

    try {
      setIsRegistrationLoading(true);
      setPaymentStage('preparing');
      const beneficiaries = selectedCandidates.map((candidate) => ({
        dni: candidate.dni,
        name: candidate.name,
        surname: candidate.surname,
        ageType: candidate.ageType,
      }));

      console.log('[FRONT][REDSYS][doPay] pollId:', registrationPoll.id);
      console.log('[FRONT][REDSYS][doPay] beneficiaries:', beneficiaries);
      console.log('[FRONT][REDSYS][doPay] selectedCandidates:', selectedCandidates);
      console.log('[FRONT][REDSYS][doPay] totalCents:', payTotalCents);

      const returnMode = window?.Capacitor?.getPlatform?.() === 'android'
        ? 'android'
        : window?.Capacitor?.getPlatform?.() === 'ios'
          ? 'ios'
          : 'web';
      const { response, data } = await CreateRedsysPayment(
        registrationPoll.id,
        beneficiaries,
        returnMode,
        gatewayMethod
      );
      console.log('[FRONT][REDSYS][doPay] backendStatus:', response.status);
      console.log('[FRONT][REDSYS][doPay] backendData:', data);
      if (!response.ok) {
        // El backend distingue: 401/403 = no hemos podido validar la sesión/perfil
        // que intenta pagar (token ausente o caducado); 5xx = fallo interno,
        // nada que ver con el usuario; el resto trae su propio data.Error
        // (encuesta cerrada, sin pago activado, importe a 0, etc.).
        if (response.status === 401 || response.status === 403) {
          showNoti(
            'Tu sesión ha caducado o no hemos podido identificar tu perfil. Vuelve a iniciar sesión e inténtalo de nuevo.',
            true,
            6000
          );
        } else if (response.status >= 500) {
          showNoti('No se ha podido iniciar el pago. Inténtalo de nuevo más tarde.', true, 5000);
        } else {
          showNoti(data?.Error || 'No se ha podido iniciar el pago.', true, 5000);
        }
        return;
      }

      setShowRegistrationModal(false);
      setIsRegistrationModalClosing(false);

      if (!data.redsys) {
        // Si el front detectó importes pendientes pero el backend no devuelve
        // pasarela, NO debemos enmascararlo como "gratis": es un descuadre real.
        if (payableSelectedCandidates.length > 0 && payTotalCents > 0) {
          showNoti(
            `El backend no devolvió pasarela aunque hay ${(payTotalCents / 100).toFixed(2)}EUR pendientes. ` +
            'Revisa que la encuesta tenga "Habilitar pago" activado y precios > 0 en las edades correctas.',
            true,
            9000
          );
        } else {
          showNoti('Inscripción guardada. No había importes pendientes para cobrar.', false);
        }
        return;
      }

      setPaymentStage('opening');
      await submitRedsys({
        redsysPayload: data.redsys,
        intentId: data.intentId,
        pollId: registrationPoll.id,
        order: data.order,
        bridgeToken: data.bridgeToken,
        receiptToken: data.receiptToken,
        gatewayMethod,
      });
    } catch (error) {
      console.error('doPay error:', error);
      showNoti('Error iniciando el pago.', true, 5000);
    } finally {
      setIsRegistrationLoading(false);
      setPaymentStage('idle');
    }
  };

  const getPaymentSummaryRows = (pollId) => {
    const registrations = registrationSummaryByPoll[pollId]?.registrations || [];
    return registrations.filter((row) =>
      row.attendanceStatus === 'registered'
      && familyDniSet.has(String(row.beneficiaryDni || ''))
    );
  };

  const handleDeleteSurvey = async (encuesta) => {
    try {
      const summary = hasPayment(encuesta)
        ? await loadRegistrationSummary(
          encuesta.id,
          familyDnis.length
            ? familyDnis
            : (await loadAccountsFromTokens()).map((account) => String(account.dni)).filter(Boolean)
        )
        : null;
      const paidRows = (summary?.registrations || []).filter((row) =>
        row.attendanceStatus === 'registered' && row.paymentStatus === 'paid'
      );
      const pendingRows = (summary?.registrations || []).filter((row) =>
        row.attendanceStatus === 'registered' && ['pending_manual', 'pending_online'].includes(row.paymentStatus)
      );

      if (paidRows.length) {
        const paidNames = paidRows
          .map((row) => `${row.beneficiaryName || ''} ${row.beneficiarySurname || ''}`.trim())
          .filter(Boolean)
          .join(', ');
        const confirmed = window.confirm(
          `Ya hay pagos registrados en "${encuesta.title}" para: ${paidNames || 'uno o varios perfiles'}. ` +
          'Si continúas, vas a cancelar la encuesta igualmente. Asegúrate al 100% antes de seguir.'
        );
        if (!confirmed) return;
      } else if (pendingRows.length) {
        const confirmed = window.confirm(
          `Esta encuesta tiene pagos pendientes o marcados para pagar en casal. ` +
          'Si continúas, cancelarás la encuesta. ¿Seguro que quieres seguir?'
        );
        if (!confirmed) return;
      }

      await borrarDeEncuesta(encuesta.id);
    } catch (error) {
      console.error('handleDeleteSurvey error:', error);
      showNoti('No se ha podido comprobar el estado del pago antes de cancelar.', true);
    }
  };

  const isSignupPoll = (encuesta) => !encuesta.questions || encuesta.questions.length === 0;
  const hasPayment = hasPaymentConfiguration;
  const getModalActionLabel = (encuesta) =>
    isSignupPoll(encuesta)
      ? (hasPayment(encuesta) ? 'Gestionar inscripción y pago' : 'Gestionar inscripción')
      : 'Gestionar pago del grupo';
  const canManageGroupPayment = (encuesta) =>
    !isSignupPoll(encuesta) && hasPayment(encuesta) && getPaymentSummaryRows(encuesta.id).length > 0;

  // Fila de inscripción/pago del propio usuario (no de toda la familia), tal
  // cual la devuelve GetPollRegistrationSummary. Ningún cálculo nuevo: solo
  // se busca dentro de lo que ya carga loadRegistrationSummary.
  const getMyRegistrationRow = (encuesta) =>
    (registrationSummaryByPoll[encuesta.id]?.registrations || [])
      .find((row) => String(row.beneficiaryDni) === String(user?.dni));

  // Estado personal para el badge visible en la tarjeta (Parte 2/3), sin
  // inventar ningún valor: si aún no ha llegado el resumen del backend, se
  // marca como 'loading' en vez de adivinar "pendiente" u otra cosa.
  const getCardPersonalStatus = (encuesta) => {
    const voted = isUserVoted(encuesta);
    if (!voted) return { status: 'none' };
    if (!hasPayment(encuesta)) return { status: 'registered' };

    const row = getMyRegistrationRow(encuesta);
    if (row) return { status: row.paymentStatus, method: row.paymentMethod, amountCents: row.amountCents, paidAt: row.paidAt };
    if (loadingRegistrationSummaryByPoll[encuesta.id] || !registrationSummaryByPoll[encuesta.id]) {
      return { status: 'loading' };
    }
    // Resumen ya cargado pero sin fila propia (caso raro / legacy): mejor
    // "sin decidir" que dar por hecho un importe que no está confirmado.
    return { status: 'unpaid' };
  };

  const closeRegistrationModal = () => {
    setIsRegistrationModalClosing(true);
    window.setTimeout(() => {
      setShowRegistrationModal(false);
      setIsRegistrationModalClosing(false);
    }, 220);
  };

  // Bloquea el scroll de la página de fondo mientras el modal de pago está
  // abierto. Sin esto, el "rubber-band"/overscroll de iOS puede dejar ver el
  // footer de llamas (u otro contenido) por debajo del modal al hacer swipe.
  useEffect(() => {
    if (!showRegistrationModal) return undefined;

    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPosition = body.style.position;
    const previousWidth = body.style.width;
    const scrollY = window.scrollY;

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.width = '100%';
    body.style.top = `-${scrollY}px`;
    // Oculta la barra de navegación inferior y el footer mientras el modal
    // está abierto (ver .payment-modal-open en index.css / BottomNav.css):
    // no basta con el z-index del modal si algo detrás crea su propio
    // contexto de apilamiento; así queda garantizado que no se vea nada
    // de la pantalla de fondo (barra inferior, footer de llamas, etc.).
    body.classList.add('payment-modal-open');

    return () => {
      body.style.overflow = previousOverflow;
      body.style.position = previousPosition;
      body.style.width = previousWidth;
      body.style.top = '';
      body.classList.remove('payment-modal-open');
      window.scrollTo(0, scrollY);
    };
  }, [showRegistrationModal]);

  return (
    <div className="encuesta-component">
      {showNotification && (
        <Notification message={notificationMessage} type={isErrorNoti ? 'error' : 'success'} />
      )}

      {verifyingPendingPayment && (
        <div className="pending-payment-banner" role="status" aria-live="polite">
          <span className="pending-payment-spinner" aria-hidden="true" />
          Verificando pago…
        </div>
      )}

      <BottomNav I18n={props.I18n} user={user} />

      <div className="recipient">
        <h1 className="titulo-page">{props.I18n.Surveys}</h1>

        {encuestas.map((encuesta, index) => {
          const detailId = `encuesta-detalle-${encuesta.id ?? index}`;
          const isOpen = activeDetalles === detailId;
          const voted = isUserVoted(encuesta);
          const cardStatus = getCardPersonalStatus(encuesta);
          const hasDeadline = Boolean(encuesta.closeDate || encuesta.date);
          const isHistorical = Boolean(encuesta.historical);

          return (
            <React.Fragment key={encuesta.id ?? index}>
              {isHistorical && index === activePollCount && <div className="answered-polls-intro"><h2 className="answered-polls-heading">ENCUESTAS CONTESTADAS</h2><p>Consulta tus respuestas a encuestas ya cerradas hasta el día del evento.</p></div>}
            <div className={`encuestas-container${isHistorical ? ' encuesta-historical' : ''}`}>
              <button
                type="button"
                className="encuesta-card-header"
                onClick={() => onClick(encuesta)}
                aria-expanded={isOpen}
                aria-controls={detailId}
              >
                <span className="encuesta-card-title-row">
                  <span className="encuesta-card-title">{encuesta.title}</span>
                  <span className={`encuesta-card-chevron${isOpen ? ' is-open' : ''}`} aria-hidden="true">⌄</span>
                </span>

                <span className="encuesta-card-meta-row">
                  <span className={`encuesta-status-chip ${isHistorical ? 'encuesta-status-closed' : 'encuesta-status-open'}`}>
                    {isHistorical ? 'Encuesta cerrada' : (props.I18n.SurveyOpen || 'Abierta')}
                  </span>
                  {hasDeadline && (
                    <span className="encuesta-card-deadline">
                      Cierra {formatDate(encuesta.closeDate || encuesta.date)}
                      {(encuesta.closeTime || encuesta.time) ? ` · ${formatTime(encuesta.closeTime || encuesta.time)}` : ''}
                    </span>
                  )}
                </span>

                <span className="encuesta-card-personal-row">
                  {cardStatus.status === 'loading' ? (
                    <span className="encuesta-personal-loading">Consultando tu inscripción…</span>
                  ) : (
                    <PaymentStatusBadge status={cardStatus.status} method={cardStatus.method} compact />
                  )}
                </span>
              </button>

              <div id={detailId} className="encuesta-detalle">
                <div className="encuesta-block">
                  <h3 className="encuesta-block-title">Información</h3>
                  <FormattedText
                    text={encuesta.description}
                    collapsible={String(encuesta.description || '').length > 220}
                  />
                  <dl className="encuesta-facts">
                    <div className="encuesta-fact">
                      <dt>{props.I18n.Date}</dt>
                      <dd>{formatDate(encuesta.date)}{encuesta.time ? ` · ${formatTime(encuesta.time)}` : ''}</dd>
                    </div>
                    {encuesta.location && (
                      <div className="encuesta-fact">
                        <dt>{props.I18n.Place}</dt>
                        <dd>{encuesta.location}</dd>
                      </div>
                    )}
                    {encuesta.closeDate && encuesta.closeTime && (
                      <div className="encuesta-fact">
                        <dt>Plazo</dt>
                        <dd>{formatDate(encuesta.closeDate)} · {formatTime(encuesta.closeTime)}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                <div className="encuesta-block encuesta-block-personal">
                  <h3 className="encuesta-block-title">Tu estado</h3>

                  {cardStatus.status === 'loading' ? (
                    <p className="encuesta-personal-loading">Consultando tu inscripción…</p>
                  ) : (
                    <>
                      <PaymentStatusBadge status={cardStatus.status} method={cardStatus.method} />

                      {cardStatus.status === 'pending_online' && (
                        <p className="encuesta-personal-copy">
                          {Number(cardStatus.amountCents) > 0 && (
                            <>Importe pendiente: <strong>{formatMoney(cardStatus.amountCents)}</strong></>
                          )}
                        </p>
                      )}
                      {cardStatus.status === 'pending_manual' && (
                        <p className="encuesta-personal-copy">Has indicado que pagarás en el casal.</p>
                      )}
                      {cardStatus.status === 'unpaid' && (
                        <p className="encuesta-personal-copy">Puedes pagar online o indicar que pagarás en el casal.</p>
                      )}
                      {cardStatus.status === 'paid' && (
                        <p className="encuesta-personal-copy">
                          {formatMoney(cardStatus.amountCents)}
                          {cardStatus.method ? ` · ${cardStatus.method === 'bizum' ? 'Bizum' : cardStatus.method === 'online' ? 'Tarjeta' : cardStatus.method}` : ''}
                          {cardStatus.paidAt ? ` · ${formatDateTime(cardStatus.paidAt)}` : ''}
                        </p>
                      )}
                    </>
                  )}

                  {voted && isSignupPoll(encuesta) && (
                    <div className="signedup-chip">
                      {(props.I18n.SignedUpTo || 'Te has apuntado a')} "{encuesta.title}"
                    </div>
                  )}
                </div>

                {voted && !hasPayment(encuesta) && (
                  <div className="encuesta-block survey-responses-container">
                    <h3 className="survey-responses-title">Resumen de inscripción</h3>
                    <div className="history-list">
                      <div className="history-card">
                        <div className="history-card-top">
                          <span className="history-card-name">{user?.name} {user?.surname}</span>
                        </div>
                        <div className="history-card-status">
                          <PaymentStatusBadge status="registered" compact />
                        </div>
                        <p className="history-card-concept">{ageLabel(user?.ageType)} · {encuesta.title}</p>
                      </div>
                    </div>
                    <p className="payment-info-note">
                      Puedes consultar o modificar tu inscripción mientras la encuesta permanezca abierta.
                    </p>
                  </div>
                )}

                {voted && (() => {
                  const answers = getUserAnswers(encuesta);
                  const paymentSummaryRows = getPaymentSummaryRows(encuesta.id);
                  const summaryInfo = registrationSummaryByPoll[encuesta.id];
                  const paymentSummaryLoading = Boolean(loadingRegistrationSummaryByPoll[encuesta.id]);

                  return (answers.length > 0 || paymentSummaryRows.length > 0 || paymentSummaryLoading) ? (
                    <>
                      {answers.length > 0 && (
                        <div className="encuesta-block survey-responses-container">
                          <h3 className="survey-responses-title">
                            {props.I18n.SurveyResponses || 'Respuestas de la encuesta'}
                          </h3>
                          <div className="user-responses">
                            {answers.map((answer, idx) => (
                              <div key={idx} className="response-item">
                                <p><strong>{props.I18n.Question || 'Pregunta'}:</strong> {answer.questionText}</p>
                                <p><strong>{props.I18n.Answer || 'Respuesta'}:</strong> {answer.response}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {paymentSummaryRows.length > 0 && hasPayment(encuesta) && (
                        <div className="encuesta-block survey-responses-container">
                          <h3 className="survey-responses-title">Resumen de pago e inscripción</h3>
                          <div className="history-list">
                            {paymentSummaryRows.map((row) => (
                              <div key={`${encuesta.id}-${row.beneficiaryDni}-${row.paymentStatus}`} className="history-card">
                                <div className="history-card-top">
                                  <span className="history-card-name">{row.beneficiaryName} {row.beneficiarySurname}</span>
                                  <strong className="history-card-amount">{formatMoney(row.amountCents)}</strong>
                                </div>
                                <div className="history-card-status">
                                  <PaymentStatusBadge status={row.paymentStatus} method={row.paymentMethod} compact />
                                  {row.paymentStatus === 'paid' && row.paymentMethod && row.paymentMethod !== 'bizum' && (
                                    <span className="history-card-method">· {paymentMethodLabel(row.paymentMethod)}</span>
                                  )}
                                </div>
                                <p className="history-card-concept">
                                  {paymentConceptLabel(row, summaryInfo?.conceptLabel || encuesta.title)}
                                </p>
                                {row.paidAt && (
                                  <p className="history-card-date">{formatDateTime(row.paidAt)}</p>
                                )}
                              </div>
                            ))}
                          </div>
                          <p className="payment-info-note">
                            Guarda este resumen si necesitas justificar un pago o revisar cualquier incidencia con la inscripción.
                          </p>
                        </div>
                      )}

                      {paymentSummaryLoading && hasPayment(encuesta) && (
                        <div className="encuesta-block survey-responses-container">
                          <p>Cargando resumen de pago...</p>
                        </div>
                      )}
                    </>
                  ) : null;
                })()}

                {isHistorical && hasPayment(encuesta) && !['paid', 'free'].includes(cardStatus.status) && getManualPaymentWindows(encuesta).length > 0 && (
                  <div className="encuesta-block manual-payment-info">
                    <h3 className="encuesta-block-title">Pago presencial pendiente</h3>
                    {getManualPaymentWindows(encuesta).filter((item) => new Date(`${item.date}T${item.endTime}:00`) >= new Date()).length > 0 ? <>
                      <p>Debes realizar el pago presencialmente en el casal.</p>
                      {getManualPaymentWindows(encuesta).filter((item) => new Date(`${item.date}T${item.endTime}:00`) >= new Date()).map((item) => <p key={`${item.date}-${item.startTime}`}><strong>{formatDate(item.date)}</strong> · {item.startTime}–{item.endTime}</p>)}
                    </> : <p>El horario indicado para el pago presencial ya ha finalizado.</p>}
                  </div>
                )}

                {!isHistorical && <div className="encuesta-block encuesta-block-actions">
                  {voted ? (
                    <>
                      {isSignupPoll(encuesta) && (
                        <button
                          className="button-default pay-button"
                          onClick={() => openRegistrationModal(encuesta)}
                          disabled={isRegistrationLoading}
                        >
                          {getModalActionLabel(encuesta)}
                        </button>
                      )}

                      {!isSignupPoll(encuesta) && hasPayment(encuesta) && (
                        <button
                          className="button-default pay-button"
                          onClick={() => openRegistrationModal(encuesta)}
                          disabled={isRegistrationLoading}
                        >
                          {getModalActionLabel(encuesta)}
                        </button>
                      )}

                      <button
                        className="button-default delete-button"
                        onClick={() => handleDeleteSurvey(encuesta)}
                        disabled={isRegistrationLoading}
                      >
                        {props.I18n.CancelSurvey}
                      </button>
                    </>
                  ) : (
                    isSignupPoll(encuesta) ? (
                      hasPayment(encuesta) ? (
                        <button className="button-default" onClick={() => openRegistrationModal(encuesta)}>
                          Inscribirme y pagar
                        </button>
                      ) : (
                        <button className="button-default" onClick={() => openRegistrationModal(encuesta)}>
                          Gestionar inscripción
                        </button>
                      )
                    ) : (
                      <>
                        <button className="button-default" onClick={() => handleAnswerSurvey(encuesta.id)}>
                          {props.I18n.AnswerSurvey || 'Contestar encuesta'}
                        </button>

                        {canManageGroupPayment(encuesta) && (
                          <button
                            className="button-default pay-button"
                            onClick={() => openRegistrationModal(encuesta)}
                            disabled={isRegistrationLoading}
                          >
                            {getModalActionLabel(encuesta)}
                          </button>
                        )}
                      </>
                    )
                  )}
                </div>}
              </div>
            </div>
            </React.Fragment>
          );
        })}
      </div>

      {showRegistrationModal && registrationPoll && (
        <div className={`modal pay-modal-overlay ${isRegistrationModalClosing ? 'is-closing' : ''}`}>
          <div className={`modal-content pay-modal ${isRegistrationModalClosing ? 'is-closing' : ''}`}>
            <div className="pay-modal-head">
              <button
                type="button"
                className="pay-modal-back"
                onClick={closeRegistrationModal}
                disabled={isRegistrationLoading}
                aria-label="Volver"
              >
                <span aria-hidden="true">‹</span>
              </button>
              <h3 className="pay-modal-title">
                {hasPayment(registrationPoll) ? 'Resumen y pago' : 'Confirmar inscripción'}
              </h3>
              <span className="pay-modal-headbar-spacer" aria-hidden="true" />
            </div>

            <div className="pay-modal-body">
              <p className="pay-modal-poll-name" title={registrationPoll.title}>{registrationPoll.title}</p>
              <p className="pay-modal-subtitle">
                {hasPayment(registrationPoll)
                  ? 'Selecciona quién paga ahora o cómo quedará registrado.'
                  : 'Selecciona quién queda apuntado a esta encuesta.'}
              </p>

              {hasPayment(registrationPoll) && (
                <PayDisclosure id="pay-info-disclosure" icon="ℹ️" summary="¿Cómo funciona el pago?">
                  <p>
                    El pago online es opcional. También puedes abonarlo presencialmente en el casal
                    dentro del plazo indicado.
                  </p>
                </PayDisclosure>
              )}

              <div className="pay-list-header">
                <span className="pay-list-heading">Personas seleccionadas</span>
                <span className="pay-list-count">
                  {selectedCandidates.length} {selectedCandidates.length === 1 ? 'persona' : 'personas'}
                </span>
              </div>

              <div className="pay-list">
                {registrationCandidates.map((candidate) => (
                  <label
                    key={candidate.dni}
                    className={`pay-row ${candidate.selected ? 'is-selected' : ''}`}
                  >
                    <span className="pay-row-main">
                      <span className="pay-check-wrap">
                        <input
                          type="checkbox"
                          checked={candidate.selected}
                          onChange={() => toggleCandidate(candidate.dni)}
                        />
                      </span>

                      <span className="pay-text">
                        <span className="pay-name-line">
                          <span className="pay-name">
                            {candidate.name} {candidate.surname}
                          </span>
                          {hasPayment(registrationPoll) && (
                            <strong className="pay-price">
                              {formatMoney(candidate.amountCents)}
                            </strong>
                          )}
                        </span>

                        <span className="pay-meta">
                          <span className="pay-tag">{ageLabel(candidate.ageType)}</span>
                          <span className={`pay-status ${hasPayment(registrationPoll) ? paymentStatusClass(candidate.status) : (candidate.status?.attendanceStatus === 'registered' ? 'status-paid' : 'status-idle')}`}>
                            {hasPayment(registrationPoll)
                              ? paymentStatusLabel(candidate.status)
                              : (candidate.status?.attendanceStatus === 'registered' ? 'Apuntado' : 'No inscrito')}
                          </span>
                        </span>
                        {hasPayment(registrationPoll) && candidate.status?.paidItems?.length > 0 && (
                          <span className="pay-meta">
                            {paymentConceptLabel(candidate.status, registrationPoll.title)}
                          </span>
                        )}
                      </span>
                    </span>
                  </label>
                ))}
              </div>

              {hasPayment(registrationPoll) && selectedCandidates.length > 0 && (
                <p className="pay-help-inline">
                  Pendiente real de cobro: <strong>{formatMoney(payableSelectedCandidates.reduce((sum, item) => sum + Number(item.amountCents || 0), 0))}</strong>.
                  Lo ya pagado o gratis no vuelve a cobrarse.
                </p>
              )}
            </div>

            <div className="pay-modal-footer pay-modal-footer-v2">
              {hasPayment(registrationPoll) && (
                <div className="pay-total-row">
                  <span>Total</span>
                  <strong>{formatMoney(payTotalCents)}</strong>
                </div>
              )}

              {hasPayment(registrationPoll) && (
                <button
                  className="pay-btn-online pay-btn-card"
                  onClick={() => doPay('card')}
                  disabled={isRegistrationLoading || !selectedCandidates.length || !payableSelectedCandidates.length}
                >
                  {(paymentStage === 'preparing' || paymentStage === 'opening') && (
                    <span className="pay-btn-spinner" aria-hidden="true" />
                  )}
                  {paymentStage === 'preparing'
                    ? 'Preparando pago…'
                    : paymentStage === 'opening'
                      ? 'Abriendo pago…'
                      : `Pagar ${formatMoney(payTotalCents)} con tarjeta`}
                </button>
              )}

              <div className="pay-actions-soft-row">
                {hasPayment(registrationPoll) && (
                  <button
                    className="pay-btn-soft"
                    onClick={() => saveRegistrationCart('manual')}
                    disabled={isRegistrationLoading || !selectedCandidates.length}
                  >
                    Pagar en casal
                  </button>
                )}

                <button
                  className="pay-btn-soft"
                  onClick={() => saveRegistrationCart('none')}
                  disabled={isRegistrationLoading}
                >
                  {hasPayment(registrationPoll)
                    ? 'Guardar y pagar después'
                    : (selectedCandidates.some((candidate) => candidate.status?.attendanceStatus === 'registered')
                      ? 'Actualizar inscripción'
                      : 'Confirmar inscripción')}
                </button>
              </div>

              {hasPayment(registrationPoll) && (
                <PayDisclosure id="pay-options-disclosure" icon="❔" summary="Diferencia entre las opciones" compact>
                  <div className="pay-disclosure-row">
                    <strong>Pagar en el casal</strong>
                    <span>Quedas apuntado y el pago se registra como presencial.</span>
                  </div>
                  <div className="pay-disclosure-row">
                    <strong>Guardar y pagar después</strong>
                    <span>Quedas apuntado, pero todavía no indicas cómo pagarás.</span>
                  </div>
                </PayDisclosure>
              )}

              <button
                type="button"
                className="pay-btn-cancel-link"
                onClick={closeRegistrationModal}
                disabled={isRegistrationLoading}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
